import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import "./appUpdateGate.scss";
import { isInWebView, postToNative } from "../utils/nativeBridge";
import { buildDismissKey, getVersionFromDevice, isVersionLess, normalizePlatform, normalizePolicy, resolveStoreUrl, } from "../utils/appUpdatePolicy";
import { runStartupRequest } from "../utils/startupRequestCache";
const API_BASE = process.env.REACT_APP_ID_API_BASE || process.env.REACT_APP_IDP_ORIGIN || "";
const EXPO_DEVICE_INFO_EVENT = "expoDeviceInfo";
function getStoredDeviceInfo() {
    try {
        return window.__EXPO_DEVICE_INFO__ || null;
    }
    catch {
        return null;
    }
}
export default function AppUpdateGate() {
    const [nativeDevice, setNativeDevice] = useState(() => getStoredDeviceInfo());
    const [policies, setPolicies] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        if (!isInWebView())
            return;
        const existing = getStoredDeviceInfo();
        if (existing) {
            setNativeDevice(existing);
        }
        const handleDeviceInfo = (event) => {
            const next = event.detail;
            if (next) {
                setNativeDevice(next);
            }
        };
        window.addEventListener(EXPO_DEVICE_INFO_EVENT, handleDeviceInfo);
        return () => {
            window.removeEventListener(EXPO_DEVICE_INFO_EVENT, handleDeviceInfo);
        };
    }, []);
    useEffect(() => {
        if (!isInWebView() || !API_BASE)
            return;
        let active = true;
        runStartupRequest(`startup:app-update-policy:${API_BASE}`, async () => {
            const response = await fetch(`${API_BASE}/api/client/app-update-policy`, {
                method: "GET",
                credentials: "include",
                cache: "no-store",
            });
            if (!response.ok) {
                return null;
            }
            return (await response.json());
        }, { cacheResult: true })
            .then((body) => {
            if (!active || !body?.items)
                return;
            setPolicies({
                ios: normalizePolicy("ios", body.items.ios),
                android: normalizePolicy("android", body.items.android),
            });
        })
            .catch(() => {
            // ignore: login page should continue to work without update policy
        });
        return () => {
            active = false;
        };
    }, []);
    const platform = useMemo(() => normalizePlatform(nativeDevice?.os), [nativeDevice?.os]);
    const appVersion = useMemo(() => getVersionFromDevice(nativeDevice), [nativeDevice]);
    const activePolicy = useMemo(() => {
        if (!platform || !policies)
            return null;
        return policies[platform];
    }, [platform, policies]);
    const storeUrl = useMemo(() => resolveStoreUrl(activePolicy, platform, nativeDevice), [activePolicy, nativeDevice, platform]);
    const isBelowRecommended = useMemo(() => {
        if (!appVersion || !activePolicy?.recommendedVersion)
            return false;
        return isVersionLess(appVersion, activePolicy.recommendedVersion);
    }, [activePolicy?.recommendedVersion, appVersion]);
    const isBelowMinimum = useMemo(() => {
        if (!appVersion || !activePolicy?.minSupportedVersion)
            return false;
        return isVersionLess(appVersion, activePolicy.minSupportedVersion);
    }, [activePolicy?.minSupportedVersion, appVersion]);
    const dismissKey = useMemo(() => {
        if (!platform || !activePolicy)
            return "";
        return buildDismissKey(platform, activePolicy);
    }, [activePolicy, platform]);
    useEffect(() => {
        if (!dismissKey) {
            setDismissed(false);
            return;
        }
        try {
            setDismissed(window.localStorage.getItem(dismissKey) === "1");
        }
        catch {
            setDismissed(false);
        }
    }, [dismissKey]);
    if (!isInWebView() || !platform || !appVersion || !activePolicy?.enabled) {
        return null;
    }
    const canOpenStore = Boolean(storeUrl);
    const shouldBlockByVersion = isBelowMinimum || (activePolicy.forceUpdate && isBelowRecommended);
    const isBlocking = shouldBlockByVersion && canOpenStore;
    const shouldShowSoftPrompt = (isBelowRecommended || isBelowMinimum) && (!shouldBlockByVersion || !canOpenStore) && !dismissed;
    if (!isBlocking && !shouldShowSoftPrompt) {
        return null;
    }
    const title = activePolicy.title ||
        (isBlocking ? "Нужно обновить приложение" : "Доступно обновление приложения");
    const message = activePolicy.message ||
        (isBlocking
            ? "Обновите приложение, чтобы продолжить работу без ошибок."
            : "Установите свежую версию приложения для стабильной работы.");
    const buttonText = activePolicy.buttonText || "Обновить";
    function handleUpdate() {
        if (!storeUrl)
            return;
        postToNative("open_app_update_link", {
            url: storeUrl,
            platform,
            version: appVersion,
            applicationId: nativeDevice?.applicationId || null,
        });
    }
    function handleDismiss() {
        if (!dismissKey) {
            setDismissed(true);
            return;
        }
        try {
            window.localStorage.setItem(dismissKey, "1");
        }
        catch {
            // ignore
        }
        setDismissed(true);
    }
    if (isBlocking) {
        return (_jsx("div", { className: "app-update-gate__backdrop", children: _jsxs("div", { className: "app-update-gate__modal", children: [_jsx("div", { className: "app-update-gate__badge", children: "\u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044F \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0435" }), _jsx("h2", { className: "app-update-gate__title", children: title }), _jsx("p", { className: "app-update-gate__message", children: message }), _jsx("button", { type: "button", className: "app-update-gate__primary", onClick: handleUpdate, children: buttonText })] }) }));
    }
    return (_jsx("div", { className: "app-update-gate__banner", children: _jsxs("div", { className: "app-update-gate__banner-content", children: [_jsxs("div", { children: [_jsx("div", { className: "app-update-gate__title", children: title }), _jsx("p", { className: "app-update-gate__message", children: message })] }), _jsxs("div", { className: "app-update-gate__actions", children: [canOpenStore ? (_jsx("button", { type: "button", className: "app-update-gate__primary", onClick: handleUpdate, children: buttonText })) : null, _jsx("button", { type: "button", className: "app-update-gate__secondary", onClick: handleDismiss, children: "\u041F\u043E\u0437\u0436\u0435" })] })] }) }));
}
